import { TextFields, ViewInAr } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ScaleIcon from '@mui/icons-material/Scale';
import { Box, Button, Card, CardActions, CardContent, Divider, Grid, IconButton, InputAdornment, LinearProgress, Paper, TextField, Typography } from "@mui/material";
import { calculateVolumeCm3, Dimensions, getTotalVolumeLabel, getTotalWeightLabel, getVolumeLabel, GoodsInfo, sumTotalWeight } from "adoms-common-lib";
import React, { useEffect, useState } from "react";

export type MaritimeDeliveryItemInputProps = {
    items: GoodsInfo[]; // controlled
    setGoodsItems: (items: GoodsInfo[]) => void;
    setIsOverCapacity?: (isOver: boolean) => void;
};

const generateId = () => {
    // Prefer crypto.randomUUID when available
    // Fallback to a simple unique string
    // @ts-ignore
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return `id_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
};

const createGoodsItem: (defaults?: Partial<GoodsInfo>) => GoodsInfo = (defaults) => ({
    weight: 0,
    name: "",
    count: 1,
    id: generateId(),
    handlingInstructions: ["指定なし"],
    packageType: "指定なし",
    ...defaults,
});

export const MaritimeDeliveryItemInput: React.FC<MaritimeDeliveryItemInputProps> = (props) => {
    const [fallbackItem] = useState<GoodsInfo>(() => createGoodsItem());

    // Ensure at least one row exists
    useEffect(() => {
        if (props.items.length > 0) return;
        props.setGoodsItems([fallbackItem]);
    }, [props.items, props.setGoodsItems, fallbackItem]);

    const goodsItems = props.items.length ? props.items : [fallbackItem];
    const isAddButtonDisabled = goodsItems.length >= 2;

    const updateItems = (nextItems: GoodsInfo[]) => {
        props.setGoodsItems(nextItems);
    };

    const handleAdd = () => {
        if (goodsItems.length >= 2) return;
        updateItems([...goodsItems, createGoodsItem()]);
    };

    const handleRemove = (goodsId: string) => {
        const nextItems = goodsItems.filter((x) => x.id !== goodsId);
        updateItems(nextItems.length ? nextItems : [createGoodsItem()]);
    };

    const handleNameChange = (goodsId: string, name: string) => {
        updateItems(goodsItems.map((x) => (x.id === goodsId ? { ...x, name } : x)));
    };

    const handleDimensionChange = (goodsId: string, dimensionType: 'length' | 'width' | 'height', newValue: string) => {
        if (newValue === "") {
            const parsedValue = 0;
            updateItems(goodsItems.map((x) => {
                if (x.id === goodsId) {
                    const baseDimensions: Dimensions = x.dimensions ?? { length: 0, width: 0, height: 0 };

                    let nextDimensions: Dimensions;
                    switch (dimensionType) {
                        case 'length':
                            nextDimensions = { ...baseDimensions, length: parsedValue };
                            break;
                        case 'width':
                            nextDimensions = { ...baseDimensions, width: parsedValue };
                            break;
                        case 'height':
                            nextDimensions = { ...baseDimensions, height: parsedValue };
                            break;
                    }

                    return {
                        ...x,
                        dimensions: nextDimensions,
                    }
                }
                return x;
            }));
            return;
        }

        const parsedValue = parseFloat(newValue);
        if (isNaN(parsedValue) || parsedValue <= 0) return;
        updateItems(goodsItems.map((x) => {
            if (x.id === goodsId) {
                const baseDimensions: Dimensions = x.dimensions ?? { length: 0, width: 0, height: 0 };

                let nextDimensions: Dimensions;
                switch (dimensionType) {
                    case 'length':
                        nextDimensions = { ...baseDimensions, length: parsedValue };
                        break;
                    case 'width':
                        nextDimensions = { ...baseDimensions, width: parsedValue };
                        break;
                    case 'height':
                        nextDimensions = { ...baseDimensions, height: parsedValue };
                        break;
                }

                return {
                    ...x,
                    dimensions: nextDimensions,
                }
            }
            return x;
        }));
    };

    const handleWeightChange = (goodsId: string, newValue: string) => {
        if (newValue === "") {
            updateItems(goodsItems.map((x) => (x.id === goodsId ? { ...x, weight: 0 } : x)));
            return;
        }
        const parsedValue = parseFloat(newValue);
        if (isNaN(parsedValue) || parsedValue <= 0) return;
        updateItems(goodsItems.map((x) => (x.id === goodsId ? { ...x, weight: parsedValue } : x)));
    };

    const totalWeight = sumTotalWeight(goodsItems);
    const totalVolumeLabel = getTotalVolumeLabel(goodsItems);
    const totalWeightLabel = getTotalWeightLabel(goodsItems);
    const initialMaxWeight = 8000;
    const usageRatio = Math.min((totalWeight / initialMaxWeight) * 100, 100);
    const isOverCapacity = totalWeight > initialMaxWeight;

    useEffect(() => {
        props.setIsOverCapacity?.(isOverCapacity);
    }, [isOverCapacity, props.setIsOverCapacity]);

    const defaultComponent = () => {
        return (
            <Grid container spacing={2}>
                {goodsItems.map((item) => {
                    return (
                        <Grid item xs={12} key={item.id}>
                            <Card variant="outlined" sx={{ border: '1.5px solid rgba(59, 63, 65, 0.2)' }}>
                                <CardContent>
                                    <Grid
                                        container
                                        spacing={2}
                                        alignItems="flex-start"
                                        direction="column"
                                        padding={1}
                                    >
                                        {/* 物品名 */}
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <TextFields fontSize="small" /> 物品名
                                            </Typography>
                                            <TextField
                                                value={item.name}
                                                onChange={(e) => handleNameChange(item.id!!, e.target.value)}
                                                size="small"
                                                fullWidth
                                            />
                                        </Grid>

                                        {/* サイズ */}
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <ViewInAr fontSize="small" /> サイズ
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, width: '100%', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                                                <TextField
                                                    size="small"
                                                    label="縦"
                                                    name="length"
                                                    type="number"
                                                    fullWidth
                                                    value={(item.dimensions?.length ?? 0) === 0 ? "" : item.dimensions?.length}
                                                    onChange={(e) => handleDimensionChange(item.id!!, 'length', e.target.value)}
                                                    InputLabelProps={{ shrink: true, sx: { fontSize: '0.8rem' } }}
                                                    InputProps={{
                                                        endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                                                    }}
                                                    inputProps={{ min: 1 }}
                                                    sx={{ flex: '1 1 0', minWidth: 0 }}
                                                />
                                                <TextField
                                                    size="small"
                                                    label="横"
                                                    name="width"
                                                    type="number"
                                                    fullWidth
                                                    value={(item.dimensions?.width ?? 0) === 0 ? "" : item.dimensions?.width}
                                                    onChange={(e) => handleDimensionChange(item.id!!, 'width', e.target.value)}
                                                    InputLabelProps={{ shrink: true, sx: { fontSize: '0.8rem' } }}
                                                    InputProps={{
                                                        endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                                                    }}
                                                    inputProps={{ min: 1 }}
                                                    sx={{ flex: '1 1 0', minWidth: 0 }}
                                                />
                                                <TextField
                                                    size="small"
                                                    label="高さ"
                                                    name="height"
                                                    type="number"
                                                    fullWidth
                                                    value={(item.dimensions?.height ?? 0) === 0 ? "" : item.dimensions?.height}
                                                    onChange={(e) => handleDimensionChange(item.id!!, 'height', e.target.value)}
                                                    InputLabelProps={{ shrink: true, sx: { fontSize: '0.8rem' } }}
                                                    InputProps={{
                                                        endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                                                    }}
                                                    inputProps={{ min: 1 }}
                                                    sx={{ flex: '1 1 0', minWidth: 0 }}
                                                />
                                            </Box>
                                            <Box
                                                sx={{
                                                    mt: 1,
                                                    p: 1,
                                                    borderRadius: 1,
                                                    bgcolor: '#f0f9ff',
                                                    border: '0.5px solid rgba(2, 132, 199, 0.2)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between'
                                                }}
                                            >
                                                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                                                    合計体積
                                                </Typography>
                                                <Typography variant="body1" fontWeight="bold" color="#0284c7">
                                                    {calculateVolumeCm3(item)} <Box component="span" sx={{ ml: 0.5 }}>cm³</Box>
                                                </Typography>
                                            </Box>
                                        </Grid>

                                        {/* 重量 */}
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <ScaleIcon fontSize="small" /> 重量
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="重量"
                                                name="weight"
                                                type="number"
                                                value={item.weight === 0 ? "" : item.weight}
                                                onChange={(e) => handleWeightChange(item.id!!, e.target.value)}
                                                InputLabelProps={{ shrink: true, sx: { fontSize: '0.8rem' } }}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">g</InputAdornment>,
                                                }}
                                                inputProps={{ min: 1 }}
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                                <CardActions>
                                    <IconButton aria-label="削除" onClick={() => handleRemove(item.id!!)} sx={{ marginLeft: 'auto' }}>
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </CardActions>
                            </Card>
                        </Grid>
                    );
                })}

                <Grid item xs={12}>
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2.5,
                            borderRadius: 1,
                            bgcolor: '#f0f9ff',
                            borderColor: '#bae6fd',
                            borderStyle: 'solid',
                            borderWidth: 1
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold" color="#0369a1">
                                全配送物の合計
                            </Typography>
                        </Box>
                        <Grid container spacing={2} direction="column">
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    合計体積
                                </Typography>
                                <Typography fontWeight="bold" color="#0284c7">
                                    {totalVolumeLabel}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    合計重量 / 搭載可能量
                                </Typography>
                                <Typography fontWeight="bold" color="#0284c7">
                                    {totalWeightLabel} / {initialMaxWeight.toLocaleString()} g
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <LinearProgress
                                    variant="determinate"
                                    value={usageRatio}
                                    color={isOverCapacity ? "error" : usageRatio > 80 ? "warning" : "primary"}
                                    sx={{ height: 8, borderRadius: 4, bgcolor: 'divider' }}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12}>
                    <Button
                        disabled={isAddButtonDisabled}
                        onClick={handleAdd}
                        variant="contained"
                        startIcon={<AddIcon />}
                        fullWidth
                    >
                        追加
                    </Button>
                </Grid>
            </Grid>
        )
    }

    const smartphoneComponent = () => {
        return (
            <Grid container spacing={2}>
                {goodsItems.map((item) => {
                    return (
                        <Grid item xs={12} key={item.id}>
                            <Card variant="outlined" sx={{ border: '1.5px solid rgba(59, 63, 65, 0.2)' }}>
                                <CardContent>
                                    <Grid container direction="column" spacing={2} alignItems="flex-start" justifyContent="center">
                                        <Grid item container xs={12} spacing={1}>
                                            <Grid item>
                                                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <TextFields fontSize="small" /> 物品名
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sx={{ width: '100%' }}>
                                                <TextField
                                                    value={item.name}
                                                    onChange={(e) => handleNameChange(item.id!!, e.target.value)}
                                                    size="small"
                                                    fullWidth
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid item container xs={12} spacing={1}>
                                            <Grid item>
                                                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <ViewInAr fontSize="small" /> サイズ
                                                </Typography>
                                            </Grid>
                                            <Grid item container spacing={2} direction={"column"} wrap="nowrap">
                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label="縦"
                                                        name="length"
                                                        type="number"
                                                        value={(item.dimensions?.length ?? 0) === 0 ? "" : item.dimensions?.length}
                                                        onChange={(e) => handleDimensionChange(item.id!!, 'length', e.target.value)}
                                                        InputLabelProps={{ shrink: true, sx: { fontSize: '0.8rem' } }}
                                                        InputProps={{
                                                            endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                                                        }}
                                                        inputProps={{ min: 1 }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        label="横"
                                                        size="small"
                                                        name="width"
                                                        type="number"
                                                        value={(item.dimensions?.width ?? 0) === 0 ? "" : item.dimensions?.width}
                                                        onChange={(e) => handleDimensionChange(item.id!!, 'width', e.target.value)}
                                                        InputLabelProps={{ shrink: true, sx: { fontSize: '0.8rem' } }}
                                                        InputProps={{
                                                            endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                                                        }}
                                                        inputProps={{ min: 1 }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label="高さ"
                                                        name="height"
                                                        type="number"
                                                        value={(item.dimensions?.height ?? 0) === 0 ? "" : item.dimensions?.height}
                                                        onChange={(e) => handleDimensionChange(item.id!!, 'height', e.target.value)}
                                                        InputLabelProps={{ shrink: true, sx: { fontSize: '0.8rem' } }}
                                                        InputProps={{
                                                            endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                                                        }}
                                                        inputProps={{ min: 1 }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Box
                                                        sx={{
                                                            p: 1,
                                                            borderRadius: 1,
                                                            bgcolor: '#f0f9ff',
                                                            border: '0.5px solid rgba(2, 132, 199, 0.2)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between'
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="body2" color="text.secondary" fontWeight="medium">
                                                                合計体積
                                                            </Typography>
                                                        </Box>
                                                        <Typography variant="body1" fontWeight="bold" color="#0284c7">
                                                            {calculateVolumeCm3(item)} <Box component="span" sx={{ ml: 0.5 }}>cm³</Box>
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item container xs={12} spacing={1}>
                                            <Grid item>
                                                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <ScaleIcon fontSize="small" /> 重量
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sx={{ width: '100%' }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="重量"
                                                    name="weight"
                                                    type="number"
                                                    value={item.weight === 0 ? "" : item.weight}
                                                    onChange={(e) => handleWeightChange(item.id!!, e.target.value)}
                                                    InputLabelProps={{ shrink: true, sx: { fontSize: '0.8rem' } }}
                                                    InputProps={{
                                                        endAdornment: <InputAdornment position="end">g</InputAdornment>,
                                                    }}
                                                    inputProps={{ min: 1 }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                                <CardActions>
                                    <IconButton aria-label="削除" onClick={() => handleRemove(item.id!!)} sx={{ marginLeft: 'auto' }}>
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </CardActions>
                            </Card>
                        </Grid>
                    )
                })}
                <Grid item xs={12}>
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2.5,
                            borderRadius: 1,
                            bgcolor: '#f0f9ff',
                            borderColor: '#bae6fd',
                            borderStyle: 'solid',
                            borderWidth: 1
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold" color="#0369a1">
                                全配送物の合計
                            </Typography>
                        </Box>
                        <Grid container spacing={2} direction="column">
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    合計体積
                                </Typography>
                                <Typography fontWeight="bold" color="#0284c7">
                                    {totalVolumeLabel}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    合計重量 / 搭載可能量
                                </Typography>
                                <Typography fontWeight="bold" color="#0284c7">
                                    {totalWeightLabel} / {initialMaxWeight.toLocaleString()} g
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <LinearProgress
                                    variant="determinate"
                                    value={usageRatio}
                                    color={isOverCapacity ? "error" : usageRatio > 80 ? "warning" : "primary"}
                                    sx={{ height: 8, borderRadius: 4, bgcolor: 'divider' }}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
                {/* 追加ボタン（スマホ用） */}
                <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12}>
                    <Button
                        disabled={isAddButtonDisabled}
                        onClick={handleAdd}
                        variant="contained"
                        startIcon={<AddIcon />}
                        fullWidth
                    >
                        追加
                    </Button>
                </Grid>
            </Grid>
        )
    }

    return (
        //xs表示はスマホ用、sm以上はPC用
        <Box>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                {defaultComponent()}
            </Box>
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                {smartphoneComponent()}
            </Box>
        </Box>
    );
};